// Streaming guard for Buffy's inline math (2026-10-02). While a reply streams,
// "$20d + " arrives before its closing "$", so the opening dollar would flash
// on screen as text. holdOpenMath cuts an unclosed math span off the end until
// its close lands. Delimiter runs pair like remark-math's: a run of N dollars
// closes only on another run of N; an escaped "\$" is literal.
// A short tail on one line is held; a long or multi-line tail is a lone
// currency dollar ("the $3 mug"), so it shows as text.
const HOLD_MAX = 80;

export function holdOpenMath(text) {
  const s = String(text ?? '');
  let open = -1;
  let size = 0;
  const runs = /\$+/g;
  let m;
  while ((m = runs.exec(s)) !== null) {
    if (m.index > 0 && s[m.index - 1] === '\\') continue;
    if (open < 0) { open = m.index; size = m[0].length; }
    else if (m[0].length === size) open = -1;
  }
  if (open < 0) return s;
  const tail = s.slice(open);
  return tail.length <= HOLD_MAX && !tail.includes('\n') ? s.slice(0, open) : s;
}

// The same guard for links (2026-10-17: a group mention streamed in as
// "[10 students](students:04687448-d011-…" with ten ids of raw text until its
// ")" landed). An unfinished "[label](target" at the end is held back, however
// long the target; a "[" with no "]" yet is held like a math tail (short, one
// line), so a lone bracket in plain text still shows.
export function holdOpenLink(text) {
  const s = String(text ?? '');
  const open = s.lastIndexOf('[');
  if (open < 0 || s[open - 1] === '\\') return s;
  const tail = s.slice(open);
  const close = tail.indexOf(']');
  if (close < 0) return tail.length <= HOLD_MAX && !tail.includes('\n') ? s.slice(0, open) : s;
  const after = tail.slice(close + 1);
  if (after === '') return s.slice(0, open);
  if (after[0] !== '(' || after.includes(')') || after.includes('\n')) return s;
  return s.slice(0, open);
}

// Buffy sometimes writes prices bare ("adds $40 to $20 for every day"), and
// remark-math paired the two into one math span: "40to20" (live teaching
// note, 2026-10-02). Pair single dollars left to right with Pandoc's rules: a
// span opens on "$" + non-space and closes on non-space + "$" that is not
// followed by a digit. It also stays on one line and does not end on an open
// bracket. A "$" that cannot open a valid span is a price, so it is escaped.
// The last unpaired "$" stays as is: it can be math still streaming in
// (holdOpenMath), and a finished lone "$" is text anyway.
const validSpan = (s, open, close) => {
  const body = s.slice(open + 1, close);
  return /\S/.test(s[open + 1] || '') && /\S/.test(s[close - 1] || '')
    && !/\d/.test(s[close + 1] || '') && !body.includes('\n') && !/[([{]$/.test(body);
};

function escapeCurrencyPart(s) {
  const marks = [];
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === '$' && s[i - 1] !== '\\') marks.push(i);
  }
  const escape = [];
  let i = 0;
  while (i < marks.length - 1) {
    if (validSpan(s, marks[i], marks[i + 1])) { i += 2; continue; }
    escape.push(marks[i]);
    i += 1;
  }
  let out = s;
  for (let j = escape.length - 1; j >= 0; j -= 1) out = `${out.slice(0, escape[j])}\\${out.slice(escape[j])}`;
  return out;
}

export function escapeCurrency(text) {
  const s = String(text ?? '');
  if (s.indexOf('$') === -1) return s;
  // $$...$$ display blocks are left to remark-math.
  return s.split(/(\$\$[\s\S]*?\$\$)/).map((part, k) => (k % 2 ? part : escapeCurrencyPart(part))).join('');
}

// Runs after rehype-katex. A line could break between rendered math and the
// punctuation touching it, leaving "." alone on the last line at phone width
// (a word joiner does not stop it). Glue the math to an opening bracket right
// before it and punctuation right after it in a nowrap span.
const LEAD = /[([“‘"]+$/;
const TRAIL = /^[.,;:!?)\]”’'"]+/;
const isMath = (n) => n?.type === 'element' && Array.isArray(n.properties?.className) && n.properties.className.includes('katex');

export function glueMathPunctuation(node) {
  const kids = node?.children;
  if (!Array.isArray(kids)) return;
  for (let i = 0; i < kids.length; i += 1) {
    const k = kids[i];
    if (!isMath(k)) { glueMathPunctuation(k); continue; }
    const prev = kids[i - 1];
    const next = kids[i + 1];
    const lead = prev?.type === 'text' ? LEAD.exec(prev.value)?.[0] : null;
    const trail = next?.type === 'text' ? TRAIL.exec(next.value)?.[0] : null;
    if (!lead && !trail) continue;
    const children = [];
    if (lead) { prev.value = prev.value.slice(0, -lead.length); children.push({ type: 'text', value: lead }); }
    children.push(k);
    if (trail) { next.value = next.value.slice(trail.length); children.push({ type: 'text', value: trail }); }
    kids[i] = { type: 'element', tagName: 'span', properties: { style: 'white-space:nowrap' }, children };
  }
}

export const rehypeGlueMath = () => glueMathPunctuation;
